import { useQueries, useQueryClient } from '@tanstack/react-query';

import { getBusinessSettings } from '~/services/BusinessTraveller/BusinessTravellerService';

import { createConcurrencyLimiter } from '~/utils/createConcurrencyLimiter';

import type { DashboardBusiness } from './useBusinessDashboard';
import { silverGrantsQueryOptions } from './useLebtSilverGrants';

// Metadata is business-level, shared with expanded rows, and bounded across filter changes.
const runLimited = createConcurrencyLimiter(2);

export function useSocieteOverviewMetadata(businesses: Array<DashboardBusiness>, enabled: boolean) {
  const queryClient = useQueryClient();
  const queries = useQueries({
    queries: businesses.map((business) => ({
      queryKey: ['business-traveller', 'societe-overview-metadata', business.id],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        runLimited(async () => {
          const [settings, grants] = await Promise.all([
            queryClient.fetchQuery({
              queryKey: ['business-traveller', 'societe-settings', business.id],
              queryFn: async () => {
                const [defaults, overrides] = await Promise.all([
                  queryClient.fetchQuery({
                    queryKey: ['business-traveller', 'societe-default-settings'],
                    queryFn: () => getBusinessSettings(),
                    staleTime: 60_000,
                  }),
                  getBusinessSettings(business.id),
                ]);
                return overrides.societe?.value ?? defaults.societe?.value ?? null;
              },
              staleTime: 60_000,
              retry: false,
            }),
            queryClient.fetchQuery(silverGrantsQueryOptions(business.id)),
          ]);
          return { settings, grants };
        }, signal),
      enabled,
      staleTime: 60_000,
      retry: false,
    })),
  });
  return businesses.map((business, index) => ({ businessId: business.id, query: queries[index] }));
}
